<template>
  <div style="height: 100%">
    <transition-group name="splashscreen">
      <div
        v-if="showSplash"
        key="splashscreen"
        class="splashscreen"
        :style="{
          backgroundImage: `url('/images/front/splashscreen.png?${splashT}')`,
        }"
      />
      <template v-else>
        <virtual-mobile-layout
          v-if="isVirtual && isMobile"
          key="virtual-mobile-layout"
        />
        <virtual-desktop-layout
          v-else-if="isVirtual"
          key="virtual-desktop-layout"
        />
        <classic-layout v-else key="classic-layout" />
        <notifications-alert
          v-if="showNotifications"
          key="notifications-alert"
        />
      </template>
    </transition-group>
    <settings-modal />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import NotificationsAlert from './components/views/notifications-alert'
import ClassicLayout from './layouts/Classic'
import VirtualDesktopLayout from './layouts/VirtualDesktop'
import VirtualMobileLayout from './layouts/VirtualMobile'
import navigateMixin from './mixins/navigate'
import scrollMixin from './mixins/scroll'
import SettingsModal from './components/settings/modal'
import './cookie_consent_init'

const speakers = () =>
  import(/* webpackChunkName: "speakers" */ './components/speakers')
const speakerDetail = () =>
  import(/* webpackChunkName: "speaker" */ './components/speaker-detail')
const partners = () =>
  import(/* webpackChunkName: "partners" */ './components/partners')
const sessions = () =>
  import(/* webpackChunkName: "sessions" */ './components/sessions')
const node = () => import(/* webpackChunkName: "node" */ './components/node')
const questions = () =>
  import(/* webpackChunkName: "questions" */ './components/questions')
const questionsForm = () =>
  import(/* webpackChunkName: "questions" */ './components/questions-form')
const content_pages = () =>
  import(/* webpackChunkName: "content_pages" */ './components/content_pages')
const feedback = () =>
  import(/* webpackChunkName: "feedback" */ './components/feedback')
const feedbackForm = () =>
  import(/* webpackChunkName: "feedback" */ './components/feedback-form')
const voting = () =>
  import(/* webpackChunkName: "voting" */ './components/voting')
const survey = () =>
  import(/* webpackChunkName: "survey" */ './components/survey')
const user_gallery = () =>
  import(/* webpackChunkName: "user_gallery" */ './components/user_gallery')
const photobooth = () =>
  import(/* webpackChunkName: "photobooth" */ './components/photobooth')
const iframe = () =>
  import(/* webpackChunkName: "iframe" */ './components/iframe')
const messages = () =>
  import(/* webpackChunkName: "messages" */ './components/messages')
const activities = () =>
  import(/* webpackChunkName: "activities" */ './components/activities')
const my_profile = () =>
  import(/* webpackChunkName: "my_profile" */ './components/my_profile')
const top = () =>
  import(/* webpackChunkName: "my_profile" */ './components/top')
const team = () => import(/* webpackChunkName: "team" */ './components/team')
const networking = {
  main: () =>
    import(/* webpackChunkName: "networking" */ './components/networking/main'),
  register: () =>
    import(
      /* webpackChunkName: "networking" */ './components/networking/register'
    ),
  profile: () =>
    import(
      /* webpackChunkName: "networking" */ './components/networking/profile'
    ),
  chat: () =>
    import(/* webpackChunkName: "networking" */ './components/networking/chat'),
}
const chat = () => import(/* webpackChunkName: "chat" */ './components/chat')
const quiz = () => import(/* webpackChunkName: "quiz" */ './components/quiz')
const instagram_gallery = () =>
  import(
    /* webpackChunkName: "instagram_gallery" */ './components/instagram_gallery'
  )
const meetings = () =>
  import(/* webpackChunkName: "meetings" */ './components/meetings')
const access_denied = () =>
  import(/* webpackChunkName: "access_denied" */ './components/access_denied')

export default {
  mixins: [navigateMixin, scrollMixin],

  components: {
    ClassicLayout,
    VirtualDesktopLayout,
    VirtualMobileLayout,
    NotificationsAlert,
    SettingsModal,
  },

  data() {
    let appLastUpdated = EasyEvent.lastUpdated

    return {
      appLastUpdated,
      showSplash: true,
      splashT: EasyEvent.splashT,
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      fullMenu: (state) => state.common.menu,
      showRoomsSelect: (state) => state.common.virtual.showRoomsSelect,
      defaultRoom: (state) => state.common.virtual.multipleRooms.defaultRoom,
      deviceId: (state) => state.common.deviceId,
    }),

    ...mapGetters('common', ['showNotifications', 'rootMenu', 'isVirtual']),

    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },
  },

  created() {
    const authPromise = this.$store.dispatch('auth/getVisitor')
    const commonPromise = this.$store.dispatch('common/getData')

    Promise.all([authPromise, commonPromise]).then(() => {
      this.createRoutes()
      this.checkVirtualDefaultRoom()
      setTimeout(() => {
        this.showSplash = false
      }, 2000)

      this.$store.dispatch('sessions/getSessions')

      this.fetchMessages()
      if (this.user) {
        this.fetchNetworkingNotifications()
      }
    })

    this.listen()

    setInterval(() => {
      this.checkAppUpdate()
    }, 60000)

    cc.runWithLocale()
  },

  methods: {
    ...mapActions(['fetchMessages', 'fetchNetworkingNotifications']),

    ...mapMutations(['addNewMessage', 'removeMessage', 'addNetworkingMessage']),

    ...mapMutations('chat', {
      addNewChatMessage: 'addMessage',
      removeChatMessage: 'removeMessage',
    }),

    ...mapMutations('common', ['saveSettings', 'changeSmileysVisibility']),

    listen() {
      socket.on(EasyEvent.channel + ':App\\Events\\NewMessage', (data) => {
        let msg = data.message
        if (msg.badge_types.length === 0) {
          this.addNewMessage(msg)
        } else if (this.user) {
          for (var i = 0; i < msg.badge_types.length; i++) {
            if (msg.badge_types[i].code === this.user.badge_type) {
              this.addNewMessage(msg)
              break
            }
          }
        }
      })
      socket.on(EasyEvent.channel + ':App\\Events\\MessageDeleted', (data) => {
        this.removeMessage(data.message)
      })
      socket.on(
        EasyEvent.channel + ':App\\Events\\NewNetworkingMessage',
        (data) => {
          if (this.user) {
            let msg = data.message

            if (msg.receiver_id === this.user.id) {
              this.addNetworkingMessage(msg)
            }
          }
        }
      )
      socket.on(
        `${EasyEvent.channel}:App\\Events\\NewChatMessage`,
        ({ message }) => {
          this.addNewChatMessage(message)
        }
      )
      socket.on(
        `${EasyEvent.channel}:App\\Events\\ChatMessageDeleted`,
        ({ message }) => {
          this.removeChatMessage(message)
        }
      )
      socket.on(
        EasyEvent.channel + ':App\\Events\\QuizQuestionStateChanged',
        ({ question }) => {
          if (question.active) {
            const menu = this.fullMenu.find(
              (item) => item.module === 'quiz' && !item.config
            )
            if (menu) {
              this.navigateTo(menu)
            }
          }
        }
      )
      socket.on(
        `${EasyEvent.channel}:App\\Events\\SettingsChanged`,
        ({ settings }) => {
          this.saveSettings(settings)
        }
      )
      socket.on(
        `${EasyEvent.channel}:App\\Events\\LogoutVisitorFromDevice`,
        ({ visitor_id, device_id }) => {
          if (
            this.user &&
            this.user.id === visitor_id &&
            device_id !== this.deviceId
          ) {
            this.logout('another_device')
          }
        }
      )
      socket.on(
        `${EasyEvent.channel}:App\\Events\\ChangeSmileysVisibility`,
        (data) => {
          this.changeSmileysVisibility(data)
        }
      )
    },

    checkAppUpdate() {
      axios
        .get('/updated')
        .then((response) => {
          if (response.data > this.appLastUpdated) {
            location.reload()
          }
        })
        .catch(() => {})
    },

    createRoutes() {
      const routes = [
        {
          path: '*',
          components: {
            viewer: () =>
              import(
                /* webpackChunkName "stream" */ './components/video-stream'
              ),
          },
        },
        {
          path: '/room-:roomId?/',
          alias: '/',
          component:
            this.isVirtual && this.rootMenu.length
              ? eval(this.rootMenu[0].module)
              : () =>
                  import(/* webpackChunkName: "home" */ './components/home'),
          props: {
            login:
              this.isVirtual && this.rootMenu.length
                ? !!this.rootMenu[0].login_required
                : false,
            module_key:
              this.isVirtual && this.rootMenu.length
                ? this.rootMenu[0].module
                : 'home',
            config:
              this.isVirtual && this.rootMenu.length
                ? this.rootMenu[0].config
                : null,
            slug:
              this.isVirtual && this.rootMenu.length
                ? this.rootMenu[0].slug
                : null,
          },
        },
        {
          path: '/showrooms',
          name: 'showrooms',
          components: {
            viewer: () =>
              import(
                /* webpackChunkName: "showrooms" */ './components/showrooms'
              ),
          },
        },
        {
          path: '/showroom/:slug',
          name: 'showroom',
          components: {
            default: () =>
              import(
                /* webpackChunkName: "showrooms" */ './components/showroom-chat'
              ),
            viewer: () =>
              import(
                /* webpackChunkName: "showrooms" */ './components/showroom-viewer'
              ),
          },
        },
      ]

      this.fullMenu.forEach((item) => {
        if (item.module === 'external_link') return

        routes.push({
          path: `/room-:roomId?/${item.slug}`,
          alias: `/${item.slug}`,
          component: !item.enabled
            ? access_denied
            : item.module === 'networking'
            ? networking.main
            : item.module === 'my_schedule'
            ? sessions
            : eval(item.module),
          name: item.name,
          module: item.module,
          props: {
            parent: item.parent,
            login: !!item.login_required,
            module_key: item.module,
            config: item.config,
            slug: item.slug,
          },
        })

        if (item.module === 'speakers') {
          routes.push({
            path: `/room-:roomId?/${item.slug}/detail/:id`,
            alias: `/${item.slug}/detail/:id`,
            name: 'speaker-detail',
            component: !item.enabled ? access_denied : speakerDetail,
            props: {
              parent: `/${item.slug}`,
              login: !!item.login_required,
            },
          })
          routes.push({
            path: `/room-:roomId?/${item.slug}/detail/:id/:parent`,
            alias: `/${item.slug}/detail/:id/:parent`,
            name: 'speaker-detail-parent',
            component: !item.enabled ? access_denied : speakerDetail,
            props: {
              login: !!item.login_required,
            },
          })
        } else if (item.module === 'feedback') {
          routes.push({
            path: `/room-:roomId?/${item.slug}/form/:id`,
            alias: `/${item.slug}/form/:id`,
            component: !item.enabled ? access_denied : feedbackForm,
            name: 'feedback-form',
            props: {
              parent: `/${item.slug}`,
              login: !!item.login_required,
            },
          })
        } else if (item.module === 'questions') {
          routes.push({
            path: `/room-:roomId?/${item.slug}/form/:id`,
            alias: `/${item.slug}/form/:id`,
            component: !item.enabled ? access_denied : questionsForm,
            name: 'questions-form',
            props: {
              parent: `/${item.slug}`,
              login: !!item.login_required,
            },
          })
          routes.push({
            path: `/room-:roomId?/${item.slug}/form/:id/:parent`,
            alias: `/${item.slug}/form/:id/:parent`,
            component: !item.enabled ? access_denied : questionsForm,
            name: 'questions-form-parent',
            props: {
              login: !!item.login_required,
            },
          })
        } else if (item.module === 'networking') {
          routes.push({
            path: `/room-:roomId?/${item.slug}/profile/:id`,
            alias: `/${item.slug}/profile/:id`,
            component: !item.enabled ? access_denied : networking.profile,
            name: 'networking-profile',
            props: {
              parent: `/${item.slug}`,
              login: !!item.login_required,
            },
          })
          routes.push({
            path: `/room-:roomId?/${item.slug}/register/:action?`,
            alias: `/${item.slug}/register/:action?`,
            component: !item.enabled ? access_denied : networking.register,
            name: 'networking-register',
            props: {
              parent: `/${item.slug}`,
              login: !!item.login_required,
            },
          })
          routes.push({
            path: `/room-:roomId?/${item.slug}/chat/:id`,
            alias: `/${item.slug}/chat/:id`,
            component: !item.enabled ? access_denied : networking.chat,
            name: 'networking-chat',
            props: {
              parent: `/${item.slug}`,
              login: !!item.login_required,
            },
          })
        } else if (item.module === 'my_profile') {
          routes.push({
            path: `/room-:roomId?/${item.slug}/top/:type?`,
            alias: `/${item.slug}/top/:type?`,
            component: !item.enabled ? access_denied : top,
            name: 'profile_top',
            props: {
              parent: `/${item.slug}`,
              login: !!item.login_required,
            },
          })
        }
      })

      this.$router.addRoutes(routes)
    },

    checkVirtualDefaultRoom() {
      if (this.isVirtual && this.showRoomsSelect && this.defaultRoom) {
        this.navigateTo(`/room-${this.defaultRoom}`)
      }
    },
  },
}
</script>
